const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { UncontrolledTooltip, Table, Container, Col, Row, Button } = require('reactstrap')
const { useFormatBytes, useBlockchainData, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, Boxes, CopyButton } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')

require('@styles/pages/blocks/index.css')

const VerifiedContracts = () => {
    const location = useLocation()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const { getVerifiedContracts, getContractsStatistics } = useBlockchainData(network.name)

    const params = new URLSearchParams(location.search)

    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [contractsData, setContractsData] = useState()
    const [statisticsData, setStatisticsData] = useState([])
    const [loading, setLoading] = useState(false)

    const isMobile = useResponsive(BREAKPOINTS.medium)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)

            try {
                const [verifiedContractsResponse, contractsStatisticsResponse] = await Promise.all([
                    getVerifiedContracts(page, limit),
                    getContractsStatistics()
                ])

                if (verifiedContractsResponse && contractsStatisticsResponse) {
                    if (verifiedContractsResponse.totalItems > 500) {
                        verifiedContractsResponse.totalPages = Math.ceil(500 / limit)
                    }
                    setContractsData(verifiedContractsResponse)

                    setStatisticsData([
                        {
                            title: 'Contracts Deployed (Total)',
                            value: contractsStatisticsResponse.totalDeployed.toLocaleString()
                        },
                        {
                            title: 'Contracts Deployed (24h)',
                            value: contractsStatisticsResponse.todayDeployed.toLocaleString()
                        },
                        {
                            title: 'Contracts Verified (Total)',
                            value: contractsStatisticsResponse.totalVerified.toLocaleString()
                        },
                        {
                            title: 'Contracts Verified (24H)',
                            value: contractsStatisticsResponse.todayVerified.toLocaleString()
                        }
                    ])
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Verified Contracts</text>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <Boxes items={statisticsData} />

                    <div className='box table-section' style={{ padding: '24px 24px 0' }}>
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <TableHeader
                                    title={`Showing the last ${Math.min(
                                        contractsData?.items.length,
                                        500
                                    )} verified contracts source code`}
                                    page={page}
                                    limit={limit}
                                    totalPages={contractsData?.totalPages}
                                />

                                <Table
                                    responsive
                                    style={
                                        isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}
                                    }
                                >
                                    <thead>
                                        <tr>
                                            <th>Address</th>
                                            <th>Contract Name</th>
                                            <th>Compliler</th>
                                            <th>Version</th>
                                            <th>Balance</th>
                                            <th>Txns</th>
                                            <th>Setting</th>
                                            <th>Verified</th>
                                            <th>
                                                Audit&nbsp;
                                                <i
                                                    id='audit-tooltip'
                                                    className='fas fa-circle-question'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                <UncontrolledTooltip placement='top' target='audit-tooltip'>
                                                    Smart Contracts Audit and Security.
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>
                                                License&nbsp;
                                                <i
                                                    id='license-tooltip'
                                                    className='fas fa-circle-question'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                <UncontrolledTooltip placement='top' target='license-tooltip'>
                                                    Contract Source Code License Type
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>Similar Contract</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contractsData?.items.map(contract => (
                                            <tr key={contract.address}>
                                                <td>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                    <span
                                                        id={`contract-${contract.address}`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/address/${contract.address}`)
                                                        }
                                                    >
                                                        {formatBytes(contract.address, 20, 9, 8)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`contract-${contract.address}`}
                                                    >
                                                        {contract.address}
                                                    </UncontrolledTooltip>
                                                    &nbsp;
                                                    <CopyButton
                                                        id={`copy-contract-${contract.address}`}
                                                        data={contract.address}
                                                        text={'Copy address'}
                                                    />
                                                </td>
                                                <td>{contract.name}</td>
                                                <td>{contract.compiler}</td>
                                                <td>{contract.version}</td>
                                                <td>
                                                    {(contract.balance / 10 ** 18).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 18
                                                    })}
                                                    &nbsp;
                                                    {network.data.token}
                                                </td>
                                                <td>{contract.totalTransactions}</td>
                                                <td>
                                                    {contract.optimizationUsed && (
                                                        <>
                                                            <div
                                                                id={`optimzation-${contract.address}`}
                                                                className='tx-status-indicator'
                                                            >
                                                                <i
                                                                    className='fas fa-bolt'
                                                                    style={{ color: 'var(--grey-color)' }}
                                                                />
                                                            </div>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`optimzation-${contract.address}`}
                                                            >
                                                                Optimization Enabled
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                    {contract.constructor != '0x' && (
                                                        <>
                                                            <div
                                                                id={`constructor-${contract.address}`}
                                                                className='tx-status-indicator'
                                                            >
                                                                <i
                                                                    className='fas fa-wrench'
                                                                    style={{ color: 'var(--grey-color)' }}
                                                                />
                                                            </div>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`constructor-${contract.address}`}
                                                            >
                                                                Constructor Arguments
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                    {!contract.optimizationUsed && contract.constructor === '0x' && (
                                                        <>-</>
                                                    )}
                                                </td>
                                                <td>{contract.verified}</td>
                                                <td>-</td>
                                                <td>{contract.licenseType}</td>
                                                <td>
                                                    <Button
                                                        onClick={() => {
                                                            history.push(
                                                                `/${network.name}/find-similar-contracts?a=${contract.address}`
                                                            )
                                                        }}
                                                        style={{
                                                            background: `var(--${network.name}-color)`,
                                                            border: 'none',
                                                            padding: '6px 10px',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        <i className='fas fa-search' />
                                                        &nbsp;
                                                        <span>Search</span>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <TableFooter
                                    limit={limit}
                                    setLimit={setLimit}
                                    page={page}
                                    setPage={setPage}
                                    totalPages={contractsData?.totalPages}
                                />
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = VerifiedContracts
